import axiosInstance from './axiosConfig';

export interface MusicStyle {
  id: number;
  name: string;
  description: string;
  prompt_template: string;
  thumbnail_url: string | null;
  genre: string | null;
  sort_order: number;
  is_active: boolean;
}

export interface MusicProject {
  id: string;
  user_id: number;
  music_style_id: number | null;
  title: string | null;
  prompt: string;
  product_info: string | null;
  model: string;
  has_vocals: boolean;
  vocal_gender: 'male' | 'female' | null;
  lyrics_mode: 'auto' | 'manual' | null;
  lyrics_text: string | null;
  audio_s3_key: string | null;
  audio_s3_url: string | null;
  jacket_s3_key: string | null;
  jacket_s3_url: string | null;
  audio_url: string | null;
  jacket_url: string | null;
  duration_seconds: number | null;
  file_size: number | null;
  status: 'pending' | 'generating_audio' | 'generating_jacket' | 'completed' | 'failed';
  error_message: string | null;
  metadata: Record<string, any> | null;
  music_style?: MusicStyle;
  created_at: string;
  updated_at: string;
}

export interface MusicModel {
  name: string;
  description: string;
  max_duration: number;
}

export interface CreateMusicProjectData {
  prompt: string;
  title?: string;
  music_style_id?: number;
  model?: string;
  product_info?: string;
  has_vocals?: boolean;
  vocal_gender?: 'male' | 'female';
  lyrics_mode?: 'auto' | 'manual';
  lyrics_text?: string;
  reference_image_s3_keys?: string[];
  reference_image_categories?: string[];
}

class MusicGenerationService {
  async getStyles(): Promise<{ success: boolean; data: MusicStyle[] }> {
    const response = await axiosInstance.get<{ success: boolean; data: MusicStyle[] }>(
      '/music/styles'
    );
    return response.data;
  }

  async getModels(): Promise<{ success: boolean; data: Record<string, MusicModel> }> {
    const response = await axiosInstance.get<{ success: boolean; data: Record<string, MusicModel> }>(
      '/music/models'
    );
    return response.data;
  }

  async createProject(data: CreateMusicProjectData): Promise<{ success: boolean; data: MusicProject; error?: string }> {
    const response = await axiosInstance.post<{ success: boolean; data: MusicProject; error?: string }>(
      '/music/projects',
      data
    );
    return response.data;
  }

  async getProjects(): Promise<{ success: boolean; data: MusicProject[] }> {
    const response = await axiosInstance.get<{ success: boolean; data: MusicProject[] }>(
      '/music/projects'
    );
    return response.data;
  }

  async getProject(projectId: string): Promise<{ success: boolean; data: MusicProject }> {
    const response = await axiosInstance.get<{ success: boolean; data: MusicProject }>(
      `/music/projects/${projectId}`
    );
    return response.data;
  }

  async getStatus(projectId: string): Promise<{ success: boolean; data: { id: string; status: string; error_message: string | null; audio_url: string | null; jacket_url: string | null } }> {
    const response = await axiosInstance.get(
      `/music/projects/${projectId}/status`
    );
    return response.data;
  }

  async deleteProject(projectId: string): Promise<{ success: boolean; message: string }> {
    const response = await axiosInstance.delete<{ success: boolean; message: string }>(
      `/music/projects/${projectId}`
    );
    return response.data;
  }
}

export default new MusicGenerationService();
